import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Plus, MagnifyingGlass } from '@phosphor-icons/react';
import { getToken } from '@/utils/auth';
import { logger } from '@/utils/logger';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const FreeToPlayTab = ({ onGamesUpdated }) => {
  const [externalGames, setExternalGames] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/external/games/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      logger.error('Failed to fetch categories:', error);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const searchExternalGames = async () => {
    setSearchLoading(true);
    try {
      const params = selectedCategory ? `?category=${selectedCategory}` : '';
      const response = await axios.get(`${API}/external/games/search${params}`);
      setExternalGames(response.data);
    } catch (error) {
      toast.error('Failed to search external games');
    } finally {
      setSearchLoading(false);
    }
  };

  const importExternalGame = async (externalId) => {
    try {
      const token = getToken();
      await axios.post(`${API}/external/games/import`, 
        { external_id: externalId, source: 'freetogame' },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Game imported successfully!');
      onGamesUpdated?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import game');
    }
  };

  return (
    <div className="p-6 pt-4">
      <div className="flex gap-4 mb-4">
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="border border-[#E4E4E7] px-3 py-2 bg-white text-sm"
        >
          <option value="">All Categories</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
        <Button onClick={searchExternalGames} disabled={searchLoading} className="bg-[#0047FF]">
          <MagnifyingGlass size={18} className="mr-2" />
          {searchLoading ? 'Searching...' : 'Search Free Games'}
        </Button>
      </div>

      <ScrollArea className="h-[400px]">
        <div className="grid grid-cols-2 gap-4">
          {externalGames.map((game) => (
            <div key={game.external_id} className="p-3 border border-[#E4E4E7] hover:bg-[#FAFAFA]">
              <img src={game.image} alt={game.name} className="w-full h-32 object-cover mb-2" />
              <p className="font-medium text-sm text-[#09090B] truncate">{game.name}</p>
              <p className="text-xs text-[#71717A] mb-2">{game.genre} • {game.platform}</p>
              <Button size="sm" onClick={() => importExternalGame(game.external_id)} className="w-full bg-[#0047FF]">
                <Plus size={16} className="mr-1" /> Import
              </Button>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};
